import { FC, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  faArrowRight,
  faArrowUpRightFromSquare,
  faLocationDot,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  TripBuilderProposalHotelMarker,
  buildTripBuilderGoogleMapsUrl,
  buildTripBuilderProposalHotels,
  buildTripBuilderRouteDisplayLabels,
  buildTripBuilderRouteStops,
  geocodeRouteLabel,
  normalizeRouteCity,
  resolveTripBuilderRouteStops,
} from 'utils/shared/tripBuilderRoute';
import { GoogleMap, InfoWindow, Marker, useGoogleMap } from '@react-google-maps/api';

import { TripPreviewSlotView } from 'utils/shared/tripResultBuilderPreview';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { i18nTB } from 'utils/shared/utils';

type MapHotel = TripBuilderProposalHotelMarker & { lat: number; lng: number };
type TripBuilderClientRouteProps = {
  slots: TripPreviewSlotView[];
  proposal: ProposalData;
  children?: ReactNode;
};

const DEFAULT_MAP_CENTER = { lat: 48.8566, lng: 2.3522 };
const DEFAULT_MAP_ZOOM = 4;

const HOTEL_MARKER_COLORS = {
  selected: '#c01f2f',
  available: '#8fb88f',
} as const;

const MARKER_WIDTH = 20;
const MARKER_HEIGHT = 26;

const geocodeHotelLocation = async (hotel: TripBuilderProposalHotelMarker, language: string) => {
  const byName = await geocodeRouteLabel(`${hotel.name}, ${hotel.city}`, language);
  if (byName) {
    return byName;
  }
  return geocodeRouteLabel(hotel.city, language);
};

const createHotelMarkerIcon = (isActive: boolean): google.maps.Icon => {
  const color = isActive ? HOTEL_MARKER_COLORS.selected : HOTEL_MARKER_COLORS.available;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><path d="M16 1C8.82 1 3 6.82 3 14c0 10.5 13 27 13 27s13-16.5 13-27C29 6.82 23.18 1 16 1z" fill="${color}" stroke="#fff" stroke-width="1.5"/></svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: MARKER_WIDTH, height: MARKER_HEIGHT } as google.maps.Size,
    anchor: { x: MARKER_WIDTH / 2, y: MARKER_HEIGHT } as google.maps.Point,
  };
};

const FitMapBounds: FC<{ positions: Array<{ lat: number; lng: number }> }> = ({ positions }) => {
  const map = useGoogleMap();

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const updateMapView = () => {
      google.maps.event.trigger(map, 'resize');
      if (!positions.length) {
        map.setCenter(DEFAULT_MAP_CENTER);
        map.setZoom(DEFAULT_MAP_ZOOM);
        return;
      }
      if (positions.length === 1) {
        map.setCenter(positions[0]);
        map.setZoom(12);
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      positions.forEach((point) => bounds.extend(point));
      map.fitBounds(bounds, 48);
    };

    const timeoutId = window.setTimeout(updateMapView, 0);
    window.addEventListener('resize', updateMapView);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', updateMapView);
    };
  }, [map, positions]);

  return null;
};

const TripBuilderHotelsMap: FC<{
  mapHotels: MapHotel[];
  mapCenter: { lat: number; lng: number };
  mapZoom: number;
  getStatusLabel: (isActive: boolean) => string;
}> = ({ mapHotels, mapCenter, mapZoom, getStatusLabel }) => {
  const [openedMarkerKey, setOpenedMarkerKey] = useState<string | null>(null);
  const positions = mapHotels.map((hotel) => ({ lat: hotel.lat, lng: hotel.lng }));

  if (typeof window.google?.maps?.Map !== 'function') {
    return null;
  }
  return (
    <GoogleMap
      mapContainerClassName="w100p h100p"
      center={mapCenter}
      zoom={mapZoom}
      options={{
        scrollwheel: false,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      }}
    >
      {mapHotels.map((hotel) => (
        <Marker
          key={hotel.key}
          position={{ lat: hotel.lat, lng: hotel.lng }}
          icon={createHotelMarkerIcon(hotel.isActive)}
          zIndex={hotel.isActive ? 200 : 100}
          onClick={() =>
            setOpenedMarkerKey((current) => (current === hotel.key ? null : hotel.key))
          }
        >
          {openedMarkerKey === hotel.key && (
            <InfoWindow onCloseClick={() => setOpenedMarkerKey(null)}>
              <div>
                <span className="text-bold">{hotel.name}</span>
                <br />
                {hotel.city}
                <br />
                {getStatusLabel(hotel.isActive)}
              </div>
            </InfoWindow>
          )}
        </Marker>
      ))}
      <FitMapBounds positions={positions} />
    </GoogleMap>
  );
};

const TripBuilderClientRoute: FC<TripBuilderClientRouteProps> = ({ slots, proposal, children }) => {
  const { t, i18n } = useTranslation(undefined, { i18n: i18nTB });
  const mapLanguage = i18n.language;
  const [isMapsReady, setIsMapsReady] = useState(
    () => typeof window.google?.maps?.Map === 'function',
  );
  const routeLabels = useMemo(
    () => buildTripBuilderRouteDisplayLabels(slots, proposal),
    [slots, proposal],
  );
  const stops = useMemo(() => buildTripBuilderRouteStops(slots, proposal), [slots, proposal]);
  const hotels = useMemo(() => buildTripBuilderProposalHotels(slots, proposal), [slots, proposal]);

  const [geocodedCoords, setGeocodedCoords] = useState<Record<string, { lat: number; lng: number }>>({});
  const [routeGeocodedCoords, setRouteGeocodedCoords] = useState<
    Record<string, { lat: number; lng: number }>
  >({});
  const requestedGeocodesRef = useRef<Set<string>>(new Set());
  const requestedRouteGeocodesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (isMapsReady) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      if (typeof window.google?.maps?.Map === 'function') {
        setIsMapsReady(true);
      }
    }, 100);

    return () => window.clearInterval(timer);
  }, [isMapsReady]);

  useEffect(() => {
    let cancelled = false;
    requestedGeocodesRef.current = new Set();
    setGeocodedCoords({});

    hotels
      .filter((hotel) => hotel.lat == null || hotel.lng == null)
      .forEach((hotel) => {
        if (requestedGeocodesRef.current.has(hotel.key)) {
          return;
        }
        requestedGeocodesRef.current.add(hotel.key);

        void geocodeHotelLocation(hotel, mapLanguage).then((coords) => {
          if (!coords || cancelled) {
            return;
          }
          setGeocodedCoords((prev) => ({
            ...prev,
            [hotel.key]: coords,
          }));
        });
      });

    return () => {
      cancelled = true;
    };
  }, [hotels, mapLanguage]);

  useEffect(() => {
    let cancelled = false;
    requestedRouteGeocodesRef.current = new Set();
    setRouteGeocodedCoords({});

    stops
      .filter((stop) => stop.lat == null || stop.lng == null)
      .forEach((stop) => {
        const key = normalizeRouteCity(stop.label);
        if (!key || requestedRouteGeocodesRef.current.has(key)) {
          return;
        }
        requestedRouteGeocodesRef.current.add(key);

        void geocodeRouteLabel(stop.label, mapLanguage).then((coords) => {
          if (!coords || cancelled) {
            return;
          }
          setRouteGeocodedCoords((prev) => ({
            ...prev,
            [key]: coords,
          }));
        });
      });

    return () => {
      cancelled = true;
    };
  }, [stops, mapLanguage]);

  const resolvedHotels = useMemo(
    () =>
      hotels.map((hotel) => {
        if (typeof hotel.lat === 'number' && typeof hotel.lng === 'number') {
          return hotel;
        }
        const geocoded = geocodedCoords[hotel.key];
        if (!geocoded) {
          return hotel;
        }
        return {
          ...hotel,
          lat: geocoded.lat,
          lng: geocoded.lng,
        };
      }),
    [hotels, geocodedCoords],
  );

  const resolvedStops = useMemo(
    () => resolveTripBuilderRouteStops(stops, routeGeocodedCoords),
    [stops, routeGeocodedCoords],
  );

  const mapHotels = useMemo(
    () =>
      resolvedHotels.filter(
        (hotel): hotel is MapHotel =>
          typeof hotel.lat === 'number' && typeof hotel.lng === 'number',
      ),
    [resolvedHotels],
  );

  const mapCenter = mapHotels[0] || DEFAULT_MAP_CENTER;
  const mapZoom = mapHotels.length ? 6 : DEFAULT_MAP_ZOOM;
  const googleMapsUrl = useMemo(
    () => buildTripBuilderGoogleMapsUrl(resolvedStops, mapLanguage),
    [resolvedStops, mapLanguage],
  );

  return (
    <div className="tb-slot-subsection w100p mt-20 tb-slot-map">
      <div className="mp-itinerary-heading" >
        <div className="mp-itinerary-heading-label text-xxs text-uppercase">{t('route')}</div>
        <h2 className="mp-itinerary-heading-title">{t('your_route')}</h2>
        <p className="mp-itinerary-heading-subtitle text-sm">{t('route_hint')}</p>
      </div>

      <div className="tb-option-row-content w100p">
        <div className="tb-client-map-overlay">
          <div className="map-wr w100p tb-client-map-canvas">
            {isMapsReady && !!hotels.length ? (
              <TripBuilderHotelsMap
                mapHotels={mapHotels}
                mapCenter={mapCenter}
                mapZoom={mapZoom}
                getStatusLabel={(isActive) =>
                  isActive ? t('selected') : t('trip_builder.hotel_map_available')
                }
              />
            ) : (
              <div className="w100p h100p display-flex flex-center">
                <span className="text-light">
                  {!hotels.length ? t('no_hotels_in_proposal') : t('select_to_see_route')}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="tb-client-map-legend flex-between w100p mt-15">
          {!!routeLabels.length ? (
            <div className="tb-client-map-legend-route">
              <FontAwesomeIcon icon={faLocationDot} className="tb-client-map-legend-pin" />
              {routeLabels.map((label, index) => (
                <Fragment key={`${label}-${index}`}>
                  {index > 0 && <FontAwesomeIcon icon={faArrowRight} className="text-light text-sm" />}
                  <span className="text-bold">{label}</span>
                </Fragment>
              ))}
            </div>
          ) : (
            <span className="text-light">{t('select_to_see_route')}</span>
          )}
          {!!googleMapsUrl && (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-bold text-nowrap tb-client-map-legend-link"
            >
              {t('open_in_google_maps')}
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ml-5" />
            </a>
          )}
        </div>

        {children}
      </div>
    </div>
  );
};

export default TripBuilderClientRoute;
